import MessageProcessor from './MessageProcessor';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

function proxiedMediaURL(value?: string | null): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  if (!/^https?:\/\//i.test(value)) return undefined;
  return `${API_URL}/embed-media?url=${encodeURIComponent(value)}`;
}

function formatEmbedTimestamp(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function colorForEmbed(embed: any): string {
  const color = Number(embed?.color);
  if (!Number.isFinite(color) || color < 0) return '#4f545c';
  return `#${Math.max(0, Math.min(0xffffff, color)).toString(16).padStart(6, '0')}`;
}

function EmbedText({ content, className = '' }: { content?: string; className?: string }) {
  if (!content) return null;
  return (
    <div className={className}>
      <MessageProcessor content={content} />
    </div>
  );
}

export default function DiscordEmbeds({ embeds }: { embeds?: any[] }) {
  const items = Array.isArray(embeds) ? embeds.filter((embed) => embed && typeof embed === 'object') : [];
  if (!items.length) return null;

  return (
    <div className="mt-2 flex max-w-[560px] flex-col gap-2">
      {items.map((embed, index) => {
        const authorIcon = proxiedMediaURL(embed.author?.icon_url);
        const thumbnail = proxiedMediaURL(embed.thumbnail?.url);
        const image = proxiedMediaURL(embed.image?.url);
        const footerIcon = proxiedMediaURL(embed.footer?.icon_url);
        const timestamp = formatEmbedTimestamp(embed.timestamp);
        const fields = Array.isArray(embed.fields) ? embed.fields : [];

        return (
          <article
            key={`embed-${index}-${embed.url || embed.title || embed.description || ''}`}
            className="overflow-hidden rounded border border-border-color bg-bg-tertiary/80"
            style={{ borderLeft: `4px solid ${colorForEmbed(embed)}` }}
          >
            <div className="flex gap-4 p-3">
              <div className="min-w-0 flex-1">
                {embed.author?.name && (
                  <div className="mb-2 flex min-w-0 items-center gap-2 text-xs font-semibold text-text-primary">
                    {authorIcon && (
                      <img src={authorIcon} alt="" className="h-6 w-6 rounded-full object-cover" loading="lazy" />
                    )}
                    {embed.author?.url ? (
                      <a href={embed.author.url} target="_blank" rel="noreferrer" className="truncate hover:underline">
                        {embed.author.name}
                      </a>
                    ) : (
                      <span className="truncate">{embed.author.name}</span>
                    )}
                  </div>
                )}

                {embed.title && (
                  <div className="mb-2 text-[15px] font-semibold leading-snug text-text-primary">
                    {embed.url ? (
                      <a href={embed.url} target="_blank" rel="noreferrer" className="text-accent-color hover:underline">
                        {embed.title}
                      </a>
                    ) : (
                      embed.title
                    )}
                  </div>
                )}

                <EmbedText content={embed.description} className="text-[14px] leading-relaxed text-text-primary" />

                {!!fields.length && (
                  <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-3">
                    {fields.map((field: any, fieldIndex: number) => (
                      <div
                        key={`${field.name || 'field'}-${fieldIndex}`}
                        className={field.inline ? 'min-w-0 sm:col-span-1' : 'min-w-0 sm:col-span-3'}
                      >
                        <div className="mb-0.5 text-[13px] font-semibold leading-snug text-text-primary">
                          {field.name}
                        </div>
                        <EmbedText content={String(field.value || '')} className="text-[13px] leading-relaxed text-text-secondary" />
                      </div>
                    ))}
                  </div>
                )}

                {image && (
                  <a href={embed.image.url} target="_blank" rel="noreferrer" className="mt-3 block overflow-hidden rounded">
                    <img src={image} alt="" className="max-h-[360px] max-w-full rounded object-contain" loading="lazy" />
                  </a>
                )}

                {(embed.footer?.text || timestamp) && (
                  <div className="mt-3 flex min-w-0 items-center gap-2 text-xs text-text-muted">
                    {footerIcon && <img src={footerIcon} alt="" className="h-5 w-5 rounded-full object-cover" loading="lazy" />}
                    <span className="truncate">
                      {[embed.footer?.text, timestamp].filter(Boolean).join(' • ')}
                    </span>
                  </div>
                )}
              </div>

              {thumbnail && (
                <a href={embed.thumbnail.url} target="_blank" rel="noreferrer" className="hidden h-20 w-20 flex-shrink-0 overflow-hidden rounded sm:block">
                  <img src={thumbnail} alt="" className="h-full w-full object-cover" loading="lazy" />
                </a>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
